import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { AddCircleOutline, CloseRounded } from '@mui/icons-material'
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FieldArrayWithId, FieldError, SubmitHandler, useFieldArray, UseFieldArrayRemove, useForm, useWatch } from 'react-hook-form'
import { useAuth0 } from '@auth0/auth0-react'
import { v4 as uuidv4 } from 'uuid'
import {
  useOrderRequestsUploadUrl,
  useSubmitOrderRequest,
  useUnauthedOrderRequestsUploadUrl,
  useUnauthedSubmitOrderRequest,
} from '../../../logic/useApiMutation'
import { AttorneyType, LenderInfo, OrderRequestDocument, OrderRequestFormInputs, PartyFields, PartyInfo, PersonInfo } from '../../../types/orderRequest'
import { useGetConfig } from '../../../logic/useApiUnauthedQuery'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { VALID_FILENAME_PATTERN } from '../../../utils/validateFilename'
import { ContractReaderPanel } from './ContractReaderPanel'
import { contractReaderFor, Filled, loginName, Reading, readingToForm } from './contractReader'
import { LenderRead, planLender, planParties, valueConflicts } from './readingMerge'
import { 
  ACCEPT, 
  AGENT_ROLES, 
  ALLOWED_EXTENSIONS,
  BUYSIDE_ROLES, 
  defaultLenders, 
  defaultParties, 
  EMAIL_PATTERN, 
  LenderSection, 
  ORDER_TYPES, 
  PartySection, 
  PHONE_PATTERN, 
  PROPERTY_TYPES, 
  SectionHeader, 
  SELLSIDE_ROLES, 
  selectFieldProps, 
  StateSelect, 
  textFieldProps, 
  ZIP_PATTERN, 
  OpenOnFocusSelect,
  checkboxEnterKeyDown,
  ATTORNEY_TYPES,
  AttorneySection,
  attorneyLabel,
  defaultAttorney,
  buildAttorneysPayload,
  buildCustomFieldsPayload,
  getRenderableCustomFields,
  isDisplayOnlyCustomField,
  TEXT_RULES
} from './OrderRequestFormComponents'

function labelSegment(key: string, path: string): string {
  if (key.startsWith("Agent")) key = key.replace("Agent", "RequestedBy")
  if (path === 'Attorneys' && (ATTORNEY_TYPES as string[]).includes(key)) return attorneyLabel(key as AttorneyType)
  if (!isNaN(Number(key))) {
    const last = path.split('›').pop()?.trim().toLowerCase() ?? ''
    if (last === 'buyer') return `Buyer ${Number(key) + 1}`
    if (last === 'seller') return `Seller ${Number(key) + 1}`
    if (last === 'lenders') return `Lender ${Number(key) + 1}`
    if (last === 'org people') return `Person ${Number(key) + 1}`
    return `Item ${Number(key) + 1}`
  }
  return key.replace(/([A-Z])/g, ' $1').trim()
}

/** Grid column count from the admin-configured width (1-12); anything else falls back to full width */
function customFieldColumns(width: string | undefined): number {
  const columns = Number(width)
  return Number.isInteger(columns) && columns >= 1 && columns <= 12 ? columns : 12
}

function collectErrors(obj: object, path = ''): { label: string; message: string; type: string }[] {
  return Object.entries(obj).flatMap(([key, val]) => {
    if (!val || typeof val !== 'object') return []
    const label = path ? `${path} › ${labelSegment(key, path)}` : labelSegment(key, path)
    if (typeof (val as any).message === 'string' && (val as any).message) {
      return [{ label, message: (val as any).message, type: String((val as any).type ?? '') }]
    }
    return collectErrors(val as object, label)
  })
}

export function OrderRequestForm({ onSuccess, unauthed = false }: { onSuccess: (RequestId: string) => void; unauthed?: boolean }) {
  const { user } = useAuth0()

  const { control, getValues, handleSubmit, setValue, unregister, formState: { errors, submitCount } } = useForm<OrderRequestFormInputs>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      IsRushOrder: false,
      OrderType: '',
      OtherOrderType: '',
      ClosingDate: '',
      RouteToId: '',
      IsCashOrder: false,
      ContractDate: '',
      AgentRole: '',
      AgentFirstName: '',
      AgentLastName: '',
      AgentEmail: '',
      AgentPhone: '',
      AgentFax: '',
      AgentCompanyName: '',
      AgentCompanyAddress1: '',
      AgentCompanyAddress2: '',
      AgentCompanyCity: '',
      AgentCompanyState: '',
      AgentCompanyZip: '',
      PropertyAddress1: '',
      PropertyAddress2: '',
      PropertyCity: '',
      PropertyState: '',
      PropertyZip: '',
      PropertyLegalDescription: '',
      PropertyTaxId: '',
      PropertyType: '',
      Parties: defaultParties,
      Lenders: [],
      Attorneys: {},
      Comments: '',
      RepresentingSide: '',
    },
  })

  const { fields: buyerFields, append: buyerAppend, remove: buyerRemove, replace: buyerReplace } = useFieldArray({ control, name: 'Parties.Buyer' })
  const { fields: sellerFields, append: sellerAppend, remove: sellerRemove, replace: sellerReplace } = useFieldArray({ control, name: 'Parties.Seller' })
  const { fields: lenderFields, append: lenderAppend, remove: lenderRemove, replace: lenderReplace } = useFieldArray({ control, name: 'Lenders' })

  // Attorneys are keyed by type (max one each) rather than a field array; this tracks which ones have been added, in the order added
  const [attorneyTypes, setAttorneyTypes] = useState<AttorneyType[]>([])
  const [attorneyMenuAnchor, setAttorneyMenuAnchor] = useState<HTMLElement | null>(null)
  const availableAttorneyTypes = ATTORNEY_TYPES.filter((type) => !attorneyTypes.includes(type))

  const [documents, setDocuments] = useState<OrderRequestDocument[]>([])
  const RequestId = useRef<string|null>(null)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const { data } = useGetConfig();
  const sendToOptions = useMemo(() => data?.org?.OrderRequestFeature?.SendToOptions?.filter(op => !op.isDefault) ?? [], [data])
  const rushOrderSwitchEnabled = data?.org?.OrderRequestFeature?.ShowRushOrderOption
  const showAttorneyStateFields = data?.org?.OrderRequestFeature?.ShowAttorneyStateFields
  const customFields = useMemo(() => getRenderableCustomFields(data?.org?.OrderRequestFeature?.Fields), [data])
  const notesLabel = data?.org?.OrderRequestFeature?.NotesLabelOverride?.trim() || "Comments / Additional Information"

  const agentRole = useWatch({ control, name: 'AgentRole' })
  const representingSide = useWatch({control, name: 'RepresentingSide'})
  let buyerRequired = BUYSIDE_ROLES.includes(agentRole as typeof AGENT_ROLES[number]) || representingSide === 'Buyer/Borrower'
  let sellerRequired = SELLSIDE_ROLES.includes(agentRole as typeof AGENT_ROLES[number]) || representingSide === 'Seller'
  let lenderRequired = agentRole === 'Lender'

  const [buyerVisible, setBuyerVisible] = useState(false)
  const [sellerVisible, setSellerVisible] = useState(false)
  const [lenderVisible, setLenderVisible] = useState(false)
  const buyerSeeded = useRef(false)
  const sellerSeeded = useRef(false)
  const lenderSeeded = useRef(false)
  const buyerFieldsCount = useRef(buyerFields.length)
  buyerFieldsCount.current = buyerFields.length
  const sellerFieldsCount = useRef(sellerFields.length)
  sellerFieldsCount.current = sellerFields.length
  const lenderFieldsCount = useRef(lenderFields.length)
  lenderFieldsCount.current = lenderFields.length

  const authedUploadUrlMutation = useOrderRequestsUploadUrl()
  const unauthedUploadUrlMutation = useUnauthedOrderRequestsUploadUrl()
  const uploadUrlMutation = unauthed ? unauthedUploadUrlMutation : authedUploadUrlMutation

  const authedSubmitMutation = useSubmitOrderRequest()
  const unauthedSubmitMutation = useUnauthedSubmitOrderRequest()
  const submitMutation = unauthed ? unauthedSubmitMutation : authedSubmitMutation
  const navigate = useNavigate();

  // Custom field errors are keyed by index, so label them from the org's field config rather than the form path
  const { CustomFields: customFieldErrors, ...fixedFieldErrors } = errors
  const customFieldErrorList = Object.entries(customFieldErrors ?? {}).flatMap(([key, fieldErrors]) => {
    const ind = Number(key)
    const error = (fieldErrors as { Value?: FieldError } | undefined)?.Value
    if (Number.isNaN(ind) || !error?.message) return []
    return [{ label: customFields[ind]?.label || `Custom Field ${ind + 1}`, message: error.message, type: String(error.type ?? '') }]
  })

  const errorList = submitCount > 0 ? [...collectErrors(fixedFieldErrors), ...customFieldErrorList].filter(({ label, type }) => {
    if (type === 'required' && !buyerRequired && label.startsWith('Parties › Buyer')) return false
    if (type === 'required' && !sellerRequired && label.startsWith('Parties › Seller')) return false
    if (type === 'required' && !lenderRequired && label.startsWith('Lenders')) return false
    return true
  }) : []

  useEffect(() => {
    if (buyerRequired) {
      setBuyerVisible(true)
      if (!buyerSeeded.current) {
        buyerSeeded.current = true
        if (buyerFieldsCount.current === 0) buyerAppend(defaultParties.Buyer[0])
      }
    } else {
      buyerSeeded.current = false
    }
  }, [buyerRequired, buyerAppend])
  useEffect(() => {
    if (sellerRequired) {
      setSellerVisible(true)
      if (!sellerSeeded.current) {
        sellerSeeded.current = true
        if (sellerFieldsCount.current === 0) sellerAppend(defaultParties.Seller[0])
      }
    } else {
      sellerSeeded.current = false
    }
  }, [sellerRequired, sellerAppend])
  useEffect(() => {
    if (lenderRequired) {
      setLenderVisible(true)
      if (!lenderSeeded.current) {
        lenderSeeded.current = true
        if (lenderFieldsCount.current === 0) lenderAppend(defaultLenders[0])
      }
    } else {
      lenderSeeded.current = false
    }
  }, [lenderRequired, lenderAppend])

  // Read with AI: fills the form from a purchase agreement. Every value stays editable. Only for
  // companies with ContractReaderEnabled, and never on the public (logged-out) form.
  const reader = unauthed ? null : contractReaderFor(data?.org?.OrderRequestFeature, data?.contractReader)
  const readerOn = reader !== null
  const [aiFilled, setAiFilled] = useState<Filled[]>([])
  // What the last reading put on the form, so a second reading can tell its own values from the agent's edits.
  const applied = useRef<{ values: Record<string, unknown>; Buyer?: PartyFields[]; Seller?: PartyFields[]; lender: Partial<Record<LenderRead, string>> }>({ values: {}, lender: {} })
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

  /** Puts one side's parties on the form; false if the agent chose to keep theirs. */
  function placeParties(side: 'Buyer' | 'Seller', incoming: PartyFields[], replace: (v: PartyFields[]) => void,
    setVisible: (v: boolean) => void, required: boolean): boolean {
    const plan = planParties(getValues(`Parties.${side}`) ?? [], incoming, applied.current[side])
    if (plan.kind === 'none') return true
    if (plan.kind === 'clear') {
      // A required section keeps one empty row; an optional one is emptied and hidden, so nothing blank is submitted.
      if (required) replace([defaultParties[side][0]])
      else { replace([]); setVisible(false) }
      applied.current[side] = undefined
      return true
    }
    const label = side === 'Buyer' ? 'buyers/borrowers' : 'sellers'
    if (plan.ask && !window.confirm(`Replace the ${label} already on the form with the ${incoming.length} named in the contract? ` +
      'Contact addresses are kept for people the contract also names' +
      (plan.droppedAddresses ? `; ${plan.droppedAddresses} address${plan.droppedAddresses > 1 ? 'es' : ''} for people it doesn't name will be removed.` : '.'))) return false
    replace(plan.parties)
    setVisible(true)
    applied.current[side] = plan.parties
    return true
  }

  /** Puts the reading's lender values on the first lender; false if the agent chose to keep theirs. */
  function placeLender(incoming: LenderInfo | null): boolean {
    const lenders = getValues('Lenders') ?? []
    const plan = planLender(lenders[0], incoming, applied.current.lender)
    if (plan.conflicts.length && !window.confirm('Replace the lender details already on the form with the ones in the contract?')) return false
    applied.current.lender = plan.applied
    if (!plan.changed) return true
    const rest = lenders.slice(1)
    if (plan.empty && !incoming && !lenderRequired) {
      // Nothing left in the first lender: drop it, keep any others, and hide the section if none remain.
      lenderReplace(rest)
      if (!rest.length) setLenderVisible(false)
    } else {
      lenderReplace([plan.next, ...rest])
      setLenderVisible(true)
    }
    return true
  }

  function applyReading(reading: Reading) {
    const patch = readingToForm(reading)
    // Values the last reading filled that this one doesn't give are cleared, unless the agent has changed them.
    for (const [name, was] of Object.entries(applied.current.values)) {
      if (name in patch.values) continue
      const key = name as keyof OrderRequestFormInputs
      if (same(getValues(key), was)) setValue(key, (typeof was === 'boolean' ? false : '') as never, { shouldDirty: true, shouldValidate: true })
    }
    // Values the agent entered or corrected by hand are only replaced if they say so, as for parties and lender.
    const conflicts = valueConflicts(patch.values, (name) => getValues(name as keyof OrderRequestFormInputs), applied.current.values)
    const keepTheirs = conflicts.length > 0 && !window.confirm(
      `Replace what you entered for ${conflicts.map((n) => patch.labels[n] ?? n).join(', ')} with the contract's?`)
    const setNow = Object.fromEntries(Object.entries(patch.values).filter(([name]) => !(keepTheirs && conflicts.includes(name))))
    for (const [name, value] of Object.entries(setNow)) {
      setValue(name as keyof OrderRequestFormInputs, value as never, { shouldDirty: true, shouldValidate: true })
    }
    applied.current.values = setNow
    // What the agent chose to keep isn't listed as filled from the contract.
    const kept = (labels: (l: string) => boolean, what: string) => {
      patch.filled = patch.filled.filter((f) => !labels(f.label))
      patch.filled.push({ label: what, value: 'Kept yours', page: null, confidence: null, quote: '', auto: true,
        why: "You chose to keep what was already on the form, so the contract's weren't filled in" })
    }
    if (keepTheirs) for (const name of conflicts) kept((l) => l === patch.labels[name], patch.labels[name] ?? name)
    if (!placeParties('Buyer', patch.buyers, buyerReplace, setBuyerVisible, buyerRequired)) kept((l) => l.startsWith('Buyer '), 'Buyers')
    if (!placeParties('Seller', patch.sellers, sellerReplace, setSellerVisible, sellerRequired)) kept((l) => l.startsWith('Seller '), 'Sellers')
    if (!placeLender(patch.lender)) kept((l) => ['Lender Name', 'Loan Amount', 'Financing Type'].includes(l), 'Lender')
    // Requested By is never filled from the contract: anyone who prepares the document could otherwise
    // set the requester's role, company or phone. Only the login fills it (email, and the name below).
    setAiFilled(patch.filled)
  }

  useEffect(() => {
    if (user?.email) setValue('AgentEmail', user.email)
  }, [user?.email, setValue])

  // The requester's name from their login, when it has one (with the contract reader turned on).
  useEffect(() => {
    if (!readerOn) return
    const name = loginName(user)
    if (!name) return
    if (!getValues('AgentFirstName')) setValue('AgentFirstName', name.FirstName)
    if (!getValues('AgentLastName')) setValue('AgentLastName', name.LastName)
  }, [readerOn, user, getValues, setValue])

  useEffect(() => {
    if (!sendToOptions.length || sendToOptions.length > 1) return

    const currentRouteToId = getValues('RouteToId')
    const hasValidSelection = !!currentRouteToId && sendToOptions.some((option) => option.id === currentRouteToId)

    if (!hasValidSelection) {
      setValue('RouteToId', sendToOptions[0].id)
    }
  }, [getValues, sendToOptions, setValue])

  async function processFiles(files: File[]) {
    setUploadErrors([])
    setUploadingCount((c) => c + files.length)

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setUploadErrors((prev) => [...prev, `File type not allowed: ${file.name}`])
        setUploadingCount((c) => c - 1)
        continue
      }
      if (!VALID_FILENAME_PATTERN.test(file.name)) {
        setUploadErrors((prev) => [...prev, `File name contains invalid characters. Only letters, numbers, spaces, hyphens, underscores, and a single dot before the extension are allowed. Please rename the file and try again: ${file.name}`])
        setUploadingCount((c) => c - 1)
        continue
      }
      try {
        const result = await uploadUrlMutation.mutateAsync({ FileExtension: ext, RequestId: RequestId.current ?? undefined })
        // Store RequestId from first upload response
        if (!RequestId.current && result.RequestId) {
          RequestId.current = result.RequestId
        }
        const uploadResponse = await fetch(result.Url, {
          method: 'PUT',
          body: file,
          headers: {
            'x-amz-server-side-encryption': 'AES256',
            'x-amz-acl': 'bucket-owner-full-control',
          },
        })
        if (!uploadResponse.ok) {
          const text = await uploadResponse.text()
          throw new Error(`S3 upload failed: ${uploadResponse.status} - ${text}`)
        }
        setDocuments((prev) => [...prev, { DocumentFileName: result.DocumentFileName, OriginalFileName: file.name }])
      } catch {
        setUploadErrors((prev) => [...prev, `Failed to upload ${file.name}. Please try again.`])
      } finally {
        setUploadingCount((c) => c - 1)
      }
    }
  }

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) return
    const files = Array.from(e.target.files)
    e.target.value = ''
    await processFiles(files)
  }

  function removeDocument(index: number) {
    setDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  const handlePartiesRemoval = ({
    partyRequired, 
    partyFields,
    partyIndex, 
    partyRemove, 
    setPartyVisibility
  } : {
    partyRequired: boolean, 
    partyFields: FieldArrayWithId<OrderRequestFormInputs>[], 
    partyIndex: number,
    partyRemove: UseFieldArrayRemove,
    setPartyVisibility: Dispatch<SetStateAction<boolean>>
  }) => {
    if (partyRequired) {
      if (partyFields.length === 1) return undefined
      else return () => partyRemove(partyIndex)
    } else {
      return () => {
        partyRemove(partyIndex)
        if (partyFields.length === 1) setPartyVisibility(false)
      }
    }
  }

  const addAttorney = (type: AttorneyType) => {
    setValue(`Attorneys.${type}`, { ...defaultAttorney })
    setAttorneyTypes((prev) => [...prev, type])
    setAttorneyMenuAnchor(null)
  }

  const removeAttorney = (type: AttorneyType) => {
    unregister(`Attorneys.${type}`)
    setAttorneyTypes((prev) => prev.filter((t) => t !== type))
  }

  // Submit handlers
  const mapPartyAddress = (p: PartyFields["Address"], data: OrderRequestFormInputs): PartyInfo["Address"] => {
    if (p.SameAsProperty) {
      return {
        SameAsProperty: true,
        Address1: data.PropertyAddress1,
        Address2: data.PropertyAddress2 || undefined,
        City: data.PropertyCity,
        State: data.PropertyState,
        Zip: data.PropertyZip,
      }
    }
    if (!p.Address1 && !p.Address2 && !p.City && !p.State && !p.Zip) {
      return undefined
    }
    return {
      SameAsProperty: false,
      Address1: p.Address1,
      Address2: p.Address2 || undefined,
      City: p.City,
      State: p.State,
      Zip: p.Zip,
    }
  }

  const mapPerson = (p: PersonInfo) => ({
    FirstName: p.FirstName,
    MiddleName: p.MiddleName || undefined,
    LastName: p.LastName,
    Email: p.Email || undefined,
    Phone: p.Phone || undefined,
  })

  const handleSubmitBuyerSellerParties = (parties: PartyFields[], data: OrderRequestFormInputs) => {
    return parties?.flatMap((party): PartyInfo[] => {
      const address = mapPartyAddress(party.Address, data)
      if (party.Type === 'organization') {
        return [{
          Type: 'organization' as const,
          OrganizationName: party.OrganizationName || undefined,
          OrganizationType: party.OrganizationType || undefined,
          Address: address,
          OrgPeople: party.OrgPeople.map(contact => mapPerson(contact))
        }]
      } else {
        return [{
          Type: party.Type,
          Primary: mapPerson(party.Primary),
          ...(party.Type === 'married' ? { Spouse: mapPerson(party.Spouse) } : {}),
          Address: address,
        }]
      }
    }) 
  }

  const onSubmit: SubmitHandler<OrderRequestFormInputs> = async (data) => {
    const result = await submitMutation.mutateAsync({
      SubmittedBy: unauthed ? "CloseSimplePortal_Unauthed" : "CloseSimplePortal_Authed",
      RequestId: RequestId.current || uuidv4(),
      IsRushOrder: data.IsRushOrder,
      OrderType: data.OrderType,
      OtherOrderType: data.OtherOrderType,
      ClosingDate: data.ClosingDate || undefined,
      RouteToId: data.RouteToId || undefined,
      IsCashOrder: data.IsCashOrder,
      ContractDate: data.ContractDate || undefined,
      RequestedBy: {
        Role: data.AgentRole,
        FirstName: data.AgentFirstName,
        LastName: data.AgentLastName,
        Email: data.AgentEmail,
        Phone: data.AgentPhone || undefined,
        Fax: data.AgentFax || undefined,
        CompanyName: data.AgentCompanyName,
        CompanyAddress1: data.AgentCompanyAddress1 || undefined,
        CompanyAddress2: data.AgentCompanyAddress2 || undefined,
        CompanyCity: data.AgentCompanyCity || undefined,
        CompanyState: data.AgentCompanyState || undefined,
        CompanyZip: data.AgentCompanyZip || undefined,
      },
      PropertyInfo: {
        Address1: data.PropertyAddress1,
        Address2: data.PropertyAddress2 || undefined,
        City: data.PropertyCity,
        State: data.PropertyState,
        Zip: data.PropertyZip,
        PropertyType: data.PropertyType || undefined,
        LegalDescription: data.PropertyLegalDescription || undefined,
        TaxId: data.PropertyTaxId || undefined,
      },
      Parties: {
        Buyer: buyerVisible ? handleSubmitBuyerSellerParties(data.Parties.Buyer, data) : [],
        Seller: sellerVisible ? handleSubmitBuyerSellerParties(data.Parties.Seller, data) : [],
      },
      Lenders: data.Lenders.map((lender) => {
        return {
          LoanAmount: lender.LoanAmount,
          LoanNumber: lender.LoanNumber,
          FinancingType: lender.FinancingType,
          LenderName: lender.LenderName||undefined,
          LenderEmail: lender.LenderEmail||undefined,
          LenderFirstName: lender.LenderFirstName||undefined,
          LenderLastName: lender.LenderLastName||undefined,
          LenderPhone: lender.LenderPhone||undefined,
          SameAsRole: lender.SameAsRole??undefined
        }
      }),
      Attorneys: showAttorneyStateFields ? buildAttorneysPayload(data.Attorneys, attorneyTypes) : undefined,
      Comments: data.Comments || undefined,
      Documents: documents,
      CustomFields: buildCustomFieldsPayload(customFields, data.CustomFields)
    })
    onSuccess(result.RequestId)

  }

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto', mt: { md: '30px', xs: 'unset' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Typography variant="h5" sx={{ fontWeight: '500', lineHeight: '22px', m: 1, mb: 2, wordBreak: 'break-word' }}>
          New Title Order Request
        </Typography>
        <Divider />
        <Typography sx={{ pl: 1, pr: 1, wordBreak: 'break-word', pb: 2, mt: 3 }}>
          Use this form to submit a new title order request to our team. Provide the transaction details, property information, borrower(s), and any lender information below. You may also attach supporting documents such as a purchase agreement or refinance authorization. Once submitted, a team member will review your request and follow up with you directly.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <form
          noValidate
          onSubmit={e=>e.preventDefault()}
        >
          {/* Demo only, and never on the public (logged-out) form: documents go to the sandbox reader. */}
          {reader && <ContractReaderPanel reader={reader} onReading={applyReading} filled={aiFilled} />}

          {/* Transaction Information */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Transaction Information" />
            {rushOrderSwitchEnabled && (
              <Box sx={{mt: 1}}>
                <Controller
                  name="IsRushOrder"
                  control={control}
                  render={({field}) => (
                    <FormControlLabel
                      control={<Checkbox checked={field.value} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
                      label="This is a Rush Order"
                    />
                  )}
                />
              </Box>
            )}
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="OrderType"
                  control={control}
                  rules={{ required: 'Type of Order is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth margin="normal" required error={!!fieldState.error}>
                      <InputLabel>Type of Order</InputLabel>
                      <OpenOnFocusSelect {...field} {...selectFieldProps} label="Type of Order">
                        {ORDER_TYPES.map((t) => <MenuItem key={t.SoftProName} value={t.SoftProName}>{t.FriendlyName}</MenuItem>)}
                      </OpenOnFocusSelect>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              {useWatch({ control, name: `OrderType` }) === "Other" && (
                <Grid item xs={12} sm={12} md={6}>
                  <Controller name="OtherOrderType" control={control} rules={{ required: 'Order type is required', ...TEXT_RULES.OtherOrderType }}
                    render={({ field, fieldState }) => (
                      <TextField {...field} {...textFieldProps} label="Please specify the type of order" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                    )}
                  />            
                </Grid>
              )}
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="ClosingDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      return value.length === 0 || value.length === 8 ? true : "Please enter a valid date"
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Closing Date"
                        value={field.value ? new AdapterDayjs().date(field.value) : null}
                        onChange={(date: Dayjs | null) => field.onChange(date ? date.format('YYYYMMDD') : '')}
                        renderInput={(params: TextFieldProps) => (
                          <TextField
                            {...params}
                            {...textFieldProps}
                            label="Closing Date"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
              {sendToOptions.length > 0 && (
                <Grid item xs={12} sm={12} md={6}>
                  <Controller
                    name="RouteToId"
                    control={control}
                    rules={{ required: 'Please select a routing option' }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth margin="normal" required error={!!fieldState.error}>
                        <InputLabel>Route To</InputLabel>
                        <OpenOnFocusSelect {...field} {...selectFieldProps} label="Route To">
                          {sendToOptions.map((opt) => <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>)}
                        </OpenOnFocusSelect>
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container columnSpacing={{ xs: 0, lg: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="ContractDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      return value.length === 0 || value.length === 8 ? true : "Please enter a valid date"
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Contract Date"
                        value={field.value ? new AdapterDayjs().date(field.value) : null}
                        onChange={(date: Dayjs | null) => field.onChange(date ? date.format('YYYYMMDD') : '')}
                        renderInput={(params: TextFieldProps) => (
                          <TextField
                            {...params}
                            {...textFieldProps}
                            label="Contract Date"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Box sx={{mt: 1}}>
                  <Controller
                    name="IsCashOrder"
                    control={control}
                    render={({field}) => (
                      <FormControlLabel
                        control={<Checkbox checked={field.value} onChange={field.onChange} inputProps={{ onKeyDown: checkboxEnterKeyDown(field) as any }} />}
                        label="This is a Cash Order"
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
              Supporting Documents (Optional) — PDF, JPG, PNG
            </Typography>
            {uploadErrors.length > 0 && (
              <Box sx={{ mb: 1 }}>
                {uploadErrors.map((err, i) => (
                  <Typography key={i} color="error" variant="body2">{err}</Typography>
                ))}
              </Box>
            )}
            {documents.length > 0 && (
              <List dense>
                {documents.map((doc, idx) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => removeDocument(idx)}>
                        <CloseRounded />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={doc.OriginalFileName} />
                  </ListItem>
                ))}
              </List>
            )}
            <Box
              onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true) }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={(e) => { if (!(e.relatedTarget instanceof Node) || !e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false) }}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragOver(false)
                if (uploadingCount > 0) return
                const files = Array.from(e.dataTransfer.files)
                if (files.length) processFiles(files)
              }}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Drag & drop files here, or
              </Typography>
              <Button component="label" variant="outlined" disabled={uploadingCount > 0}>
                {uploadingCount > 0
                  ? <><CircularProgress size={16} sx={{ mr: 1 }} />Uploading...</>
                  : 'Choose Files'}
                <input type="file" accept={ACCEPT} hidden multiple onChange={handleFileChange} />
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Requested By */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Requested By" />
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller
                  name="AgentRole"
                  control={control}
                  rules={{ required: 'Your Role is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth={true} margin="normal" required error={!!fieldState.error} sx={{ minWidth: 240 }}>
                      <InputLabel>Your Role</InputLabel>
                      <OpenOnFocusSelect {...field} {...selectFieldProps} label="Your Role">
                        {AGENT_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                      </OpenOnFocusSelect>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              {(agentRole === "Other" || agentRole === "Attorney")  && (
                <Grid item xs={12} sm={12} md={6}>
                  <Controller
                    name="RepresentingSide"
                    control={control}
                    rules={{ required: "Please specify which side of the transaction you are representing" }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth={true} margin="normal" required error={!!fieldState.error} sx={{ minWidth: 240 }}>
                        <InputLabel>Which side of the transaction are you representing?</InputLabel>
                        <OpenOnFocusSelect {...field} {...selectFieldProps} label="Representing">
                          {['Buyer/Borrower', 'Seller'].map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </OpenOnFocusSelect>
                        {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}                  />
                </Grid>
              )}
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentFirstName" control={control} rules={{ required: 'First Name is required', ...TEXT_RULES.Name }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="First Name" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentLastName" control={control} rules={{ required: 'Last Name is required', ...TEXT_RULES.Name }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="Last Name" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentEmail" control={control} rules={{ required: 'Email is required', pattern: EMAIL_PATTERN, ...TEXT_RULES.Email }}
                  render={({ field, fieldState }) => (
                    !unauthed ?
                      <TextField
                        {...field}
                        {...textFieldProps}
                        label="Email"
                        type="email"
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                          tabIndex: -1,
                        }}
                        inputProps={{ tabIndex: -1 }}
                        variant="filled"
                        sx={{
                          '& .MuiFilledInput-root': {
                            backgroundColor: 'action.hover',
                            cursor: 'default',
                            '&:hover': { backgroundColor: 'action.hover' },
                            '&.Mui-focused': { backgroundColor: 'action.hover' },
                            '&::after': { display: 'none' },
                            '&::before': { display: 'none' },
                          },
                          '& .MuiFilledInput-input': { cursor: 'default' },
                        }}
                      /> : 
                      <TextField {...field} {...textFieldProps} label="Email" type="email" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentPhone" control={control} rules={{ pattern: PHONE_PATTERN }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="Phone" error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentFax" control={control} rules={{ pattern: PHONE_PATTERN }}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Fax" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentCompanyName" control={control} rules={{ required: 'Company Name is required', ...TEXT_RULES.CompanyName }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="Company Name" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentCompanyAddress1" control={control} rules={TEXT_RULES.Address}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company Address 1" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="AgentCompanyAddress2" control={control} rules={TEXT_RULES.Address}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company Address 2" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={4}>
                <Controller name="AgentCompanyCity" control={control} rules={TEXT_RULES.City}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company City" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <StateSelect name="AgentCompanyState" control={control} />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Controller name="AgentCompanyZip" control={control} rules={{ pattern: ZIP_PATTERN }}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Company Zip Code" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Property Information */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Property Information" />
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="PropertyAddress1" control={control} rules={{ required: 'Address 1 is required', ...TEXT_RULES.Address }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="Address 1" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="PropertyAddress2" control={control} rules={TEXT_RULES.Address}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Address 2" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
            </Grid>
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={4}>
                <Controller name="PropertyCity" control={control} rules={{ required: 'City is required', ...TEXT_RULES.City }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="City" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <StateSelect name="PropertyState" control={control} required />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <Controller name="PropertyZip" control={control} rules={{ required: 'Zip is required', pattern: ZIP_PATTERN }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} {...textFieldProps} label="Zip" required error={!!fieldState.error} helperText={fieldState.error?.message} />
                  )}
                />
              </Grid>
            </Grid>
            <Controller name="PropertyLegalDescription" control={control} rules={TEXT_RULES.LegalDescription}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label="Brief Legal Description" multiline rows={2} error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
            <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="PropertyTaxId" control={control} rules={TEXT_RULES.TaxId}
                  render={({ field, fieldState }) => <TextField {...field} {...textFieldProps} label="Tax ID" error={!!fieldState.error} helperText={fieldState.error?.message} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Controller name="PropertyType" control={control}
                  rules={{ required: 'Property type is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth margin="normal" required error={!!fieldState.error}>
                      <InputLabel>Property Type</InputLabel>
                      <OpenOnFocusSelect {...field} {...selectFieldProps} label="Property Type">
                        {PROPERTY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </OpenOnFocusSelect>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Borrower Information */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Buyer/Borrower Information" />
            {buyerVisible && buyerFields.map((field, idx) => (
              <PartySection
                contactType='Buyer'
                key={`Buyer${field.id}-${buyerRequired}`}
                index={idx}
                control={control}
                required={buyerRequired}
                onRemove={handlePartiesRemoval({
                  partyRequired: buyerRequired, 
                  partyIndex: idx, 
                  partyFields: buyerFields, 
                  partyRemove: buyerRemove, 
                  setPartyVisibility: setBuyerVisible
                })}
              />
            ))}
            <Button
              startIcon={<AddCircleOutline />}
              onClick={() => {
                if (!buyerVisible) {
                  setBuyerVisible(true)
                  if (buyerFields.length === 0) buyerAppend(defaultParties.Buyer[0])
                } else {
                  buyerAppend(defaultParties.Buyer[0])
                }
              }}
              variant="outlined"
              size="small"
            >
              Add Buyer/Borrower
            </Button>
          </Box>

          <Divider />

          {/* Seller Information */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Seller Information" />
            {sellerVisible && sellerFields.map((field, idx) => (
              <PartySection
                contactType='Seller'
                key={`Seller${field.id}-${sellerRequired}`}
                index={idx}
                control={control}
                required={sellerRequired}
                onRemove={handlePartiesRemoval({
                  partyRequired: sellerRequired, 
                  partyIndex: idx, 
                  partyFields: sellerFields, 
                  partyRemove: sellerRemove, 
                  setPartyVisibility: setSellerVisible
                })}
              />
            ))}
            <Button
              startIcon={<AddCircleOutline />}
              onClick={() => {
                if (!sellerVisible) {
                  setSellerVisible(true)
                  if (sellerFields.length === 0) sellerAppend(defaultParties.Seller[0])
                } else {
                  sellerAppend(defaultParties.Seller[0])
                }
              }}
              variant="outlined"
              size="small"
            >
              Add Seller
            </Button>
          </Box>

          <Divider />

          {/* Lender Information */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title="Lender Information" />
            {lenderVisible && lenderFields.map((field, idx) => (
              <LenderSection 
                key={`Lender${field.id}-${lenderRequired}`}
                index={idx} 
                control={control}
                required={lenderRequired}
                onRemove={handlePartiesRemoval({
                  partyRequired: lenderRequired,
                  partyIndex: idx,
                  partyFields: lenderFields,
                  // The last reading's lender values belong to the first row; once it's removed they
                  // mustn't be compared against whichever lender moves up.
                  partyRemove: ((i?: number | number[]) => {
                    if (i === 0 || (Array.isArray(i) && i.includes(0)) || i === undefined) applied.current.lender = {}
                    lenderRemove(i)
                  }) as UseFieldArrayRemove,
                  setPartyVisibility: setLenderVisible
                })}
              />
            ))}
            <Button
              startIcon={<AddCircleOutline />}
              onClick={() => {
                if (!lenderVisible) setLenderVisible(true)
                lenderAppend(defaultLenders[0])
              }}

              variant="outlined"
              size="small"
            >
              Add Lender/Loan
            </Button>
          </Box>

          <Divider />

          {/* Attorney Information */}
          {showAttorneyStateFields && (
            <>
              <Box sx={{ p: 2, mb: 1 }}>
                <SectionHeader title="Attorney Information" />
                {attorneyTypes.map((type) => (
                  <AttorneySection
                    key={`Attorney${type}`}
                    attorneyType={type}
                    control={control}
                    onRemove={() => removeAttorney(type)}
                  />
                ))}
                <Button
                  startIcon={<AddCircleOutline />}
                  onClick={(e) => setAttorneyMenuAnchor(e.currentTarget)}
                  disabled={availableAttorneyTypes.length === 0}
                  variant="outlined"
                  size="small"
                >
                  Add Attorney
                </Button>
                <Menu
                  anchorEl={attorneyMenuAnchor}
                  open={!!attorneyMenuAnchor}
                  onClose={() => setAttorneyMenuAnchor(null)}
                >
                  {availableAttorneyTypes.map((type) => (
                    <MenuItem key={type} onClick={() => addAttorney(type)}>{attorneyLabel(type)}</MenuItem>
                  ))}
                </Menu>
              </Box>

              <Divider />
            </>
          )}

          {/* Custom Fields */}
          {customFields && customFields.length > 0 &&
            <>
              <Box sx={{ p: 2, mb: 1 }}>
                <Grid container columnSpacing={{ xs: 0, lg: 2 }}>
                  {customFields.map(( customField, ind ) => (
                    <Grid item xs={12} sm={12} md={isDisplayOnlyCustomField(customField) ? 12 : customFieldColumns(customField.width)} key={'customfield'+ind}>
                      {customField.type === 'divider' && <Divider sx={{my: 2}} />}
                      {customField.type === 'label' && <Box sx={{my: 1}}><SectionHeader title={customField.label} /></Box>}
                      {!isDisplayOnlyCustomField(customField) &&
                      <Controller name={`CustomFields.${ind}.Value`} control={control} rules={{ required: customField.required ? `${customField.label} is required` : false, ...TEXT_RULES.CustomFieldValue }} defaultValue=""
                        render={( ({field, fieldState}) => {

                          if (customField.type === 'text' || customField.type === 'text area') {
                            return ( <TextField {...field} {...textFieldProps} name={customField.label} label={customField.label} multiline={customField.type === 'text area' ? true : false} rows={customField.type === 'text area' ? 4 : 1} required={customField.required} error={!!fieldState.error} helperText={fieldState.error?.message} />)
                          }


                          if (customField.type === 'select') {
                            return (
                              <Box>
                                <FormControl margin='normal' sx={{width: "100%"}} required={customField.required} error={!!fieldState.error}>
                                  <InputLabel id={`customfield-${ind}-label`}>{customField.label}</InputLabel>
                                  <OpenOnFocusSelect
                                    {...field}
                                    {...selectFieldProps}
                                    name={customField.label}
                                    key={customField.label+ind}
                                    labelId={`customfield-${ind}-label`}
                                    label={customField.label}
                                    sx={{width: '100%', ...selectFieldProps.sx, '& .MuiSelect-select': {whiteSpace: 'normal', wordBreak: 'break-word'}}}
                                  >
                                    {customField.Options && customField.Options.map(opt => (
                                      <MenuItem
                                        key={opt}
                                        value={opt}
                                        sx={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                          {opt}
                                      </MenuItem>
                                    ))}
                                  </OpenOnFocusSelect>
                                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                                </FormControl>
                              </Box>
                            )
                          }

                          return (<></>)
                        })} />}
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />
            </>
          }

          {/* Comments */}
          <Box sx={{ p: 2, mb: 1 }}>
            <SectionHeader title={notesLabel} />
            <Controller name="Comments" control={control} rules={TEXT_RULES.Comments}
              render={({ field, fieldState }) => (
                <TextField {...field} {...textFieldProps} label={notesLabel} multiline rows={4} error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Box>

          {errorList.length > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Please correct the following before submitting</AlertTitle>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {errorList.map(({ label, message }) => (
                  <li key={label}>
                    <strong>{label}</strong> — {message}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {submitMutation.isError && (
            <Alert severity='error' sx={{ mb: 3 }}>
              <Typography color="error" sx={{ mr: 2, alignSelf: 'center' }}>
                There was an error submitting your request. Please refresh the page and try again, or contact support for assistance.
              </Typography>
            </Alert>
          )}

          <Grid container display="flex" sx={{ pb: 2, justifyContent: 'center' }}>
            <Button
              type="button"
              variant="contained"
              disabled={errorList.length > 0 || submitMutation.isPending || uploadingCount > 0}
              onClick={() => handleSubmit(onSubmit)()}
            >
              {submitMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Grid>
        </form>
        <Grid container justifyContent="flex" sx={{ justifyContent: 'center' }}>
          <Button type="button" variant='text' onClick={() => navigate('/order-requests')}>Cancel</Button>
        </Grid>
      </Box>
    </Box>
  )

}
